import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {
  Body,
  Button,
  Headline,
  Icon,
  Modal,
  PoppaysLoader,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import usePurchaseProcessStartedEvent from '@contentlab/hooks/analytics/use-purchase-process-started-event';
import useStoreFlowStartedEvent from '@contentlab/hooks/analytics/use-store-flow-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {
  getContentLabWebPaymentFlowId,
  removeContentLabWebPaymentFlowId,
} from '@/utils/LtidStorage';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import getCookieValue from '@/utils/getCookieValue';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import PlansCollection from '@/types/models/pricing/plansCollection';
import SubscriptionPlan from '@/types/models/pricing/subscriptionPlan';
import PricingBundle from '@/views/content-studio/pricing/components/pricing-bundle';
import {
  ContactSalesButton,
  PricingCtaButton,
} from '@/views/content-studio/pricing/components/pricing-bundle/Buttons';
import MultiPlansPricingBundle from '@/views/content-studio/pricing/components/pricing-bundle/MultiPlansPricingBundle';
import {isFreePlan} from '@/views/content-studio/pricing/utils';
import ContentLabLogo from '@/components/ContentLabLogo';
import Link from '@/components/link';
import useContentStudioCancelSubscriptionMutation from '@/hooks/content-studio/mutations/useContentStudioCancelSubscriptionMutation';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useBrandId from '@/hooks/use-brand-id';
import useContentLabSubscriptionData from '@/hooks/use-content-lab-subscription-data';
import useHandlePricingCtaClick from '@/hooks/use-handle-pricing-cta-click';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import {contentStudioActions, ErrorKind} from '@/stores/contentStudioStore';
import styles from './ContentStudioPricingPage.module.scss';

declare global {
  interface Window {
    ChiliPiper?: {
      submit: (company: string, router: string, options: any) => void;
    };
  }
}

function getBundlePriceFootnote(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STUDIO) {
    return translate('Billed monthly');
  }
  if (bundle === ContentLabPlanBundle.ENTERPRISE) {
    return translate('Custom billing and package');
  }
  return '';
}

function getBundleDescription(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STUDIO) {
    return translate('For solo creators');
  }
  if (bundle === ContentLabPlanBundle.BUSINESS) {
    return translate('For companies and teams');
  }
  return translate('For agencies and organizations');
}

function getBundleLabel(bundle: ContentLabPlanBundle) {
  if (bundle === ContentLabPlanBundle.STUDIO) {
    return translate('Studio');
  }
  if (bundle === ContentLabPlanBundle.BUSINESS) {
    return translate('Business');
  }
  return translate('Enterprise');
}

function CancelSubscriptionModalContent({
  planName,
  cycleEndDate,
}: {
  planName: string;
  cycleEndDate: Date;
}) {
  const formattedCycleEndDate = cycleEndDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return (
    <div className={styles.modalContent}>
      <Headline size="md">
        {translate('cancel-subscription-title', {planName})}
      </Headline>
      <Body size="md">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: translate('cancel-subscription-description', {
              type: planName,
              date: formattedCycleEndDate,
            }),
          }}
        />
      </Body>
    </div>
  );
}

function BottomSubscriptionInfo({
  activeSubscription,
  cancelSubscriptionHandler,
}: {
  activeSubscription?: ContentLabSubscription;
  cancelSubscriptionHandler: () => void;
}) {
  if (!activeSubscription || isFreePlan(activeSubscription)) return null;
  const isCancelledSubscription = !activeSubscription.isAutoRenewEnabled;
  if (isCancelledSubscription) {
    return (
      <div className={styles.bottomBar}>
        <Body size="md">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate('full-access-until', {
                date: new Date(activeSubscription.renewAtMs).toLocaleDateString(
                  'en-US',
                  {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }
                ),
              }),
            }}
          />
        </Body>
      </div>
    );
  }
  // active subscription and auto renew enabled
  return (
    <div className={styles.bottomBar}>
      <Body size="md">{translate('Want to end your subscription?')}</Body>
      <Body
        size="md"
        className={styles.clickableText}
        onClick={cancelSubscriptionHandler}
      >
        {translate('Cancel now')}
      </Body>
    </div>
  );
}

function ContentStudioPricingPage() {
  const brandId = useBrandId();
  const {dashboardUser} = useDashboardUserQuery({});
  const navigation = useNavigation();
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const {storeFlowStarted} = useStoreFlowStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {purchaseProcessStarted} = usePurchaseProcessStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {uiInteraction} = useUiInteractionEvent();
  let presentation_id = '';

  const skippedFlowStartedEventSources = [
    CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
  ];

  const {
    activePlan,
    activeSubscription,
    plansCollections,
    isPlansDataFetched,
    isFetchingCurrentSubscription,
  } = useContentLabSubscriptionData();

  const {
    mutateAsync: cancelSubscription,
    isLoading: isCancelSubscriptionInProgress,
  } = useContentStudioCancelSubscriptionMutation();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const cancelSubscriptionHandler = () => {
    setIsCancelModalOpen(true);
  };

  useEffect(() => {
    // Use the same pricing flow_id if the user was redirected to web payments
    // We check it here and not in ContentStudio since ReactRouter rendering from inside-out and this happens first
    const storedFlowId = getContentLabWebPaymentFlowId();
    if (storedFlowId) {
      AnalyticsService.createFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING, {
        flow_id: storedFlowId,
      });
    }
    removeContentLabWebPaymentFlowId();

    // Assuming the flow has been initialized with the source before redirecting to the pricing page.
    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_STUDIO.PRICING
    );
    if (!flow.source || !skippedFlowStartedEventSources.includes(flow.source)) {
      storeFlowStarted({
        flow_id: flow.flow_id,
        product_type: 'subscription',
        source: flow.source || '',
      });
    }

    presentation_id = uuid();
    storeViewPresented({
      presentation_id,
      view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });

    // runs when component unmounts
    return () => {
      sendStoreViewDismissedEvent(presentation_id);
    };
  }, []);

  const handleBackClick = () => {
    const previousPath = previousLocation?.pathname;
    if (
      previousPath.startsWith(ROUTES.CONTENT_LAB) &&
      previousPath !== ROUTES.CONTENT_LAB_PRICING
    ) {
      navigation.goBack();
    } else {
      navigation.navigate(ROUTES.CONTENT_LAB);
    }
  };

  const sendStoreViewDismissedEvent = (presentationId: string) => {
    storeViewDismissed({
      presentation_id: presentationId,
      view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });
  };

  const sendUiInteractionEvent = useCallback(
    (buttonName: string) => {
      uiInteraction({
        item_name: buttonName,
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.PRICING,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
        presentation_id,
        interaction_id: uuid(),
      });
    },
    [presentation_id]
  );

  const sendPurchaseProcessStartedEvent = useCallback(
    (product_id: string, process_id?: string) => {
      purchaseProcessStarted({
        presentation_id,
        process_id: process_id || uuid(),
        product_id,
        product_name: product_id,
      });
    },
    [presentation_id]
  );

  const {handleCtaClick} = useHandlePricingCtaClick({
    shouldSkipUpgradeModal: true,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
  });

  const onContactSalesClick = async () => {
    try {
      if (!window.ChiliPiper) {
        console.error('ChiliPiper is not loaded');
        return;
      }
      sendUiInteractionEvent('Contact Sales');
      sendPurchaseProcessStartedEvent('enterprise');
      AnalyticsService.dischargeEventBatch();

      const [displayNameFirstName, displayNameLastName] = (
        dashboardUser.displayName || ''
      ).split(' ');

      // Redirect link does not work on local dev due to regex check - it does work on staging/production environments.
      const dynamicRedirectLink = `${window.location.protocol}//${window.location.hostname}${ROUTES.CONTENT_LAB}?brandId=${brandId}`;
      const agencyName = dashboardUser.agency?.name || '';
      const brandName = dashboardUser.currentBrand?.name || '';
      const agencyBrandName = [agencyName, brandName]
        .filter(Boolean)
        .join(' - ');
      const reasonForUpgrade = `Need Enterprise Package`;

      window.ChiliPiper.submit('lightricks', 'content-lab-d15b6o', {
        trigger: 'ThirdPartyForm',
        dynamicRedirectLink,
        lead: {
          email: dashboardUser.email || '',
          firstname: dashboardUser.firstName || displayNameFirstName || '',
          lastname: dashboardUser.lastName || displayNameLastName || '',
          country: getCookieValue('x-country-code') || 'US',
          company: agencyBrandName || '',
          reasonForUpgrade,
        },
        onError: (error: unknown) => {
          console.error('ChiliPiper form submission failed:', error);
        },
      });
    } catch (error) {
      console.error('Error submitting to ChiliPiper:', error);
    }
  };

  const renderSubscribeButton = (currentPlan: SubscriptionPlan) => {
    const buttonText =
      currentPlan.id === activePlan?.id ? `Current Plan` : `Get Started`;
    return (
      <PricingCtaButton
        onClick={() =>
          currentPlan
            ? handleCtaClick(
                activeSubscription?.id,
                activePlan,
                currentPlan,
                buttonText,
                false
              )
            : {}
        }
        isHighlighted={false}
        isDisabled={currentPlan?.isDisabled || false}
        isCurrentPlan={currentPlan.id === activePlan?.id}
        text={translate(buttonText)}
      />
    );
  };

  if (!isPlansDataFetched || isFetchingCurrentSubscription)
    return <PoppaysLoader />;

  const handleCancelSubscription = async () => {
    const showErrorToast = () => {
      raiseContentStudioError(
        'Failed to cancel subscription, please contact support',
        ErrorKind.FAILURE
      );
    };
    try {
      if (!activeSubscription?.id) {
        showErrorToast();
        return;
      }

      await cancelSubscription(activeSubscription.id);

      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: translate('Subscription cancelled successfully'),
      });
    } catch (error) {
      showErrorToast();
      if (error instanceof Error) {
        Sentry.captureException(error, {
          title: 'Error cancelling subscription',
          activeSubscription,
          activePlan,
        });
      }
    } finally {
      setIsCancelModalOpen(false);
    }
  };

  return (
    <div className={styles.pricingPage}>
      <div className={styles.topBar}>
        <Button
          appearance="neutral"
          mode="plain"
          size="medium"
          className={styles.backButton}
          icon={
            <Icon
              size="medium"
              appearance="neutral"
              name="Actions-Arrow-Back-Small"
            />
          }
          onClick={handleBackClick}
        >
          {translate('Back')}
        </Button>
        <Link to={ROUTES.CONTENT_LAB} className={styles.CSLogo}>
          <ContentLabLogo />
        </Link>
      </div>
      <Headline size="lg" className={styles.title}>
        {translate(`Find the plan that’s right for you`)}
      </Headline>
      <div className={styles.pricingBundles}>
        {plansCollections?.map((plansCollection: PlansCollection) => {
          if (plansCollection.plans.length === 1)
            return (
              <PricingBundle
                key={plansCollection.id}
                plan={plansCollection.plans[0]}
                bundle={plansCollection.bundle}
                name={getBundleLabel(plansCollection.bundle)}
                description={getBundleDescription(plansCollection.bundle)}
                priceFootnote={getBundlePriceFootnote(plansCollection.bundle)}
                isActiveBundle={plansCollection.bundle === activePlan?.bundle}
                button={renderSubscribeButton(plansCollection.plans[0])}
              />
            );
          if (plansCollection.plans.length > 1)
            return (
              <MultiPlansPricingBundle
                key={plansCollection.id}
                plans={plansCollection.plans}
                bundle={plansCollection.bundle}
                name={getBundleLabel(plansCollection.bundle)}
                description={getBundleDescription(plansCollection.bundle)}
                isHighlighted={
                  plansCollection.bundle === ContentLabPlanBundle.BUSINESS
                }
                isActiveBundle={plansCollection.bundle === activePlan?.bundle}
                activePlan={activePlan}
                activeSubscriptionId={activeSubscription?.id}
                sendUiInteractionEvent={sendUiInteractionEvent}
                sendPurchaseProcessStartedEvent={
                  sendPurchaseProcessStartedEvent
                }
              />
            );
          if (plansCollection.bundle === ContentLabPlanBundle.ENTERPRISE)
            return (
              <PricingBundle
                key={plansCollection.id}
                plan={null}
                bundle={plansCollection.bundle}
                name={getBundleLabel(plansCollection.bundle)}
                description={getBundleDescription(plansCollection.bundle)}
                priceFootnote={getBundlePriceFootnote(plansCollection.bundle)}
                isActiveBundle={false}
                button={
                  <ContactSalesButton
                    onContactSalesClick={onContactSalesClick}
                  />
                }
              />
            );
          return null;
        })}
      </div>
      <BottomSubscriptionInfo
        activeSubscription={activeSubscription}
        cancelSubscriptionHandler={cancelSubscriptionHandler}
      />
      <Modal
        open={isCancelModalOpen}
        showCloseButton
        handleClose={() => setIsCancelModalOpen(false)}
        handleSecondaryButtonClick={() => setIsCancelModalOpen(false)}
        primaryButtonText={translate(
          'cancel-subscription-modal-primary-button-text',
          {
            planName: getBundleLabel(activePlan?.bundle ?? ''),
          }
        )}
        primaryButtonProps={{
          className: styles.cancelSubscriptionButton,
          disabled: isCancelSubscriptionInProgress,
        }}
        handlePrimaryButtonClick={handleCancelSubscription}
        secondaryButtonText={translate('Go back')}
        size="large"
      >
        <CancelSubscriptionModalContent
          planName={getBundleLabel(activePlan?.bundle ?? '')}
          cycleEndDate={new Date()}
        />
      </Modal>
    </div>
  );
}

export default ContentStudioPricingPage;
